import { useCallback, useEffect, useRef } from 'react';
import employeeService from '@/services/employee/employee-service';

export type AvailabilityField = 'EmailAddress' | 'MobileNumber';

export interface AvailabilityResponse {
    EmailAvailable: boolean;
    MobileAvailable: boolean;
    EmailMessage: string | null;
    MobileMessage: string | null;
}

const debounceMS = 400;

const fallbackMessages: Record<AvailabilityField, string> = {
    EmailAddress: 'There is already an employee with that email address.',
    MobileNumber: 'There is already an employee with that mobile number.'
};

export default function useEmployeeAvailability(
    onResolved: (token: string, field: AvailabilityField, message: string | null) => void
) {
    const onResolvedRef = useRef(onResolved);
    const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
    const requestIDsRef = useRef(new Map<string, number>());
    const lastAnswersRef = useRef(new Map<string, { value: string; message: string | null }>());
    const mountedRef = useRef(true);

    useEffect(() => {
        onResolvedRef.current = onResolved;
    });

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            timersRef.current.forEach((timer) => clearTimeout(timer));
            timersRef.current.clear();
        };
    }, []);

    const discard = (token: string) => {
        const timer = timersRef.current.get(token);
        if (timer) clearTimeout(timer);
        timersRef.current.delete(token);
        requestIDsRef.current.set(token, (requestIDsRef.current.get(token) ?? 0) + 1);
    };

    const cancelCheck = useCallback((token: string) => {
        discard(token);
        lastAnswersRef.current.delete(token);
    }, []);

    const cancelAll = useCallback(() => {
        Array.from(requestIDsRef.current.keys()).forEach(discard);
        lastAnswersRef.current.clear();
    }, []);

    const queueCheck = useCallback(
        (token: string, field: AvailabilityField, value: string, employeeID: any = null) => {
            discard(token);

            const trimmed = (value || '').trim();
            if (!trimmed) {
                lastAnswersRef.current.delete(token);
                return;
            }

            const lastAnswer = lastAnswersRef.current.get(token);
            if (lastAnswer && lastAnswer.value === trimmed) {
                onResolvedRef.current(token, field, lastAnswer.message);
                return;
            }

            const requestID = requestIDsRef.current.get(token) ?? 0;

            timersRef.current.set(
                token,
                setTimeout(async () => {
                    timersRef.current.delete(token);

                    const response: AvailabilityResponse = await employeeService.checkAvailability({
                        EmailAddress: field === 'EmailAddress' ? trimmed : null,
                        MobileNumber: field === 'MobileNumber' ? trimmed : null,
                        EmployeeID: employeeID ?? null
                    });

                    if (!mountedRef.current || (requestIDsRef.current.get(token) ?? 0) !== requestID) return;

                    const available = field === 'EmailAddress' ? response.EmailAvailable : response.MobileAvailable;
                    const message = field === 'EmailAddress' ? response.EmailMessage : response.MobileMessage;
                    const resolved = available ? null : message || fallbackMessages[field];

                    lastAnswersRef.current.set(token, { value: trimmed, message: resolved });
                    onResolvedRef.current(token, field, resolved);
                }, debounceMS)
            );
        },
        []
    );

    return { queueCheck, cancelCheck, cancelAll };
}
